import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GitBranch, Loader2, Activity, TrendingUp, Users, FileStack, Sparkles, Leaf, TreePine } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, rememberRedirect } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const IMPACT = { High: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10", Medium: "text-brand border-white/10 bg-white/5", Low: "text-sec border-white/10 bg-white/5" };

const DIMENSIONS = ["Quality", "Economics", "Efficiency", "Experience", "Knowledge", "QoL"];
const DIM_DESC = {
  Quality: "My Kind learns from every build to raise output quality — sharper blueprints, cleaner software, and higher-fidelity results with each iteration.",
  Economics: "It optimizes cost and value continuously — cheaper to build, fairer pricing in your currency, and new ways for you to earn from your own data.",
  Efficiency: "Shared, reusable infrastructure removes waste and time — what took teams and months takes minutes, for anyone, anywhere.",
  Experience: "Every interaction is hyper-personalised and experiential — adapting to your language, culture and context so it feels made for you.",
  Knowledge: "My Kind compounds collective, anonymized knowledge into better recommendations for everyone, while you retain ownership of your data.",
  QoL: "Quality of Life — the north star. My Kind exists to make life easier, healthier, more lucrative and well, transforming how we live, work and grow together.",
};

export default function Insights() {
  const { t, user } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [carbon, setCarbon] = useState(null);
  const [dim, setDim] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) { rememberRedirect(); nav("/auth"); return; }
    if (!user) return;
    api.get("/insights").then((r) => setData(r.data)).finally(() => setLoading(false));
    api.get("/carbon").then((r) => setCarbon(r.data)).catch(() => {});
  }, [user, nav]);

  const maxCount = Math.max(1, ...(data?.top_industries || []).map((i) => i.count));

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><GitBranch size={14} /> Logic Layer</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">{t("nav_insights")}</h1>
        <p className="text-sec mt-3">The first-of-its-kind, universal <b className="text-[var(--rud-text)]">Sixth-Generation</b> app — hyper-personalised, precisioned and experiential — built to transform the way we live. My Kind consumes all the data flowing through it and continuously improves six dimensions, then proposes its own upgrades.</p>
        <div className="flex flex-wrap gap-2 mt-4" data-testid="dimensions">
          {DIMENSIONS.map((d) => (
            <button key={d} data-testid={`dim-${d}`} onClick={() => setDim(dim === d ? null : d)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${dim === d ? "bg-brand text-black" : "bg-elevated border border-rud text-sec hover:text-[var(--rud-text)]"}`}>
              {d}
            </button>
          ))}
        </div>
        {dim && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rud-card rounded-xl p-4 mt-3 text-sm" data-testid="dim-desc">
            <span className="text-brand font-semibold">{dim}. </span><span className="text-sec">{DIM_DESC[dim]}</span>
          </motion.div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sec mt-16"><Loader2 className="animate-spin" /> Analyzing patterns…</div>
      ) : data && (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            <Stat icon={Users} label="Builders" value={data.stats.users} />
            <Stat icon={FileStack} label="Blueprints architected" value={data.stats.blueprints} />
            <Stat icon={Activity} label="Signals tracked" value={data.stats.events} />
          </div>

          {carbon && (
            <div className="rud-card rounded-2xl p-6 mt-4 relative overflow-hidden" data-testid="carbon-card">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, #34D399 0%, transparent 70%)", opacity: 0.18, filter: "blur(40px)" }} />
              <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><Leaf size={20} className="text-emerald-400" /> Collective Carbon Impact</h3>
              <p className="text-sec text-sm mb-4">{carbon.message}</p>
              <div className="grid sm:grid-cols-4 gap-3">
                <div className="rounded-lg bg-elevated border border-rud p-4"><div className="text-xs text-sec">CO₂ saved (all)</div><div className="font-serif text-3xl text-emerald-400">{carbon.collective_kg}<span className="text-sm text-sec"> kg</span></div></div>
                <div className="rounded-lg bg-elevated border border-rud p-4"><div className="text-xs text-sec">Your contribution</div><div className="font-serif text-3xl">{carbon.personal_kg}<span className="text-sm text-sec"> kg</span></div></div>
                <div className="rounded-lg bg-elevated border border-rud p-4"><div className="text-xs text-sec flex items-center gap-1"><TreePine size={12} /> Trees / yr</div><div className="font-serif text-3xl">{carbon.trees_equivalent}</div></div>
                <div className="rounded-lg bg-elevated border border-rud p-4"><div className="text-xs text-sec">Km not driven</div><div className="font-serif text-3xl">{carbon.km_not_driven}</div></div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-4 mt-6">
            <div className="rud-card rounded-2xl p-6" data-testid="top-industries">
              <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><TrendingUp size={20} className="text-brand" /> Most-requested industries</h3>
              {data.top_industries.length === 0 ? <p className="text-sec text-sm">No blueprints yet — generate one in the Engine.</p> : (
                <div className="space-y-3">
                  {data.top_industries.map((i) => (
                    <div key={i.industry}>
                      <div className="flex justify-between text-sm mb-1"><span>{i.industry}</span><span className="text-sec">{i.count}</span></div>
                      <div className="h-1.5 rounded-full bg-elevated overflow-hidden"><div className="h-full bg-brand" style={{ width: `${(i.count / maxCount) * 100}%` }} /></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rud-card rounded-2xl p-6">
              <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><Activity size={20} className="text-brand" /> Activity signals</h3>
              <div className="flex flex-wrap gap-2">
                {data.event_breakdown.map((e) => (
                  <span key={e.type} className="text-xs px-3 py-1.5 rounded-full bg-elevated border border-rud">{e.type} · {e.count}</span>
                ))}
              </div>
            </div>
          </div>

          <h2 className="font-serif text-3xl tracking-tight mt-14 mb-5 flex items-center gap-2"><Sparkles className="text-brand" /> My Kind proposes</h2>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="proposals">
            {data.proposals.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="rud-card rounded-2xl p-6">
                <div className="flex items-start justify-between gap-3">
                  <h4 className="font-serif text-2xl leading-tight">{p.title}</h4>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${IMPACT[p.impact] || IMPACT.Medium}`}>{p.impact}</span>
                </div>
                <p className="text-sec text-sm mt-2 leading-relaxed">{p.rationale}</p>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon: I, label, value }) {
  return (
    <div className="rud-card rounded-2xl p-6">
      <I className="text-brand mb-3" />
      <div className="text-sec text-xs uppercase tracking-widest">{label}</div>
      <div className="font-serif text-4xl mt-1">{value}</div>
    </div>
  );
}
